import React, { Suspense, useState, useEffect, useRef } from 'react';

import LayoutContainer from './components/layout/layoutContainer';
import { ThemeProvider } from '@mui/material/styles';
import LinearProgress from '@mui/material/LinearProgress';
import { Loading } from './components';
import { getThemeHostPrefix } from './helpers';
import { styled } from '@mui/material/styles';
import useThemeStore from './stores/useThemeStore';
// MSAL imports
import { InteractionType, EventType } from "@azure/msal-browser";
import { useMsal, useIsAuthenticated, AuthenticatedTemplate, MsalAuthenticationTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { AuthError } from './components/AuthError';
import { AccessDenied } from './components/AccessDenied';
import { clearInitialAuthError, getInitialAuthError } from './authErrorStore';
import { logoutForAtlasAccessDenied } from './services/auth.service';
import { useAtlasAccessStore } from './stores/useAtlasAccessStore';
// MSAL imports
// Query hooks
import { usePermissions } from './hooks/queries/useImpersonation';
import type { PermissionsResponse } from './services/impersonation.service';
import { useImpersonationStore } from './stores/useImpersonationStore';
import { UserSelectionModal } from './components/impersonation/UserSelectionModal';
import { useQueryClient } from '@tanstack/react-query';
import { registerAtlasAccess } from './services/atlasAccess.service';


const CustomLinearProgress = styled(LinearProgress)({
    margin: '-8px',
    '&.MuiLinearProgress-colorPrimary': {
        backgroundColor: '#e6375a'
    },
    '& .MuiLinearProgress-barColorPrimary': {
        backgroundColor: '#fff'
    }
});

function App() {
    const [userName, setUserName] = useState('');
    // Fatal auth error (redirect failure or login/token failure event). When set,
    // render an error screen instead of MsalAuthenticationTemplate, which would
    // otherwise re-fire the login redirect and loop indefinitely.
    const [authError, setAuthError] = useState<unknown>(() => getInitialAuthError());
    const { isAccessDenied, reason, clearAccessDenied } = useAtlasAccessStore();
    const queryClient = useQueryClient();
    const { instance } = useMsal();
    const callbackIdRef = useRef(null);

    // Theme loading logic
    const { theme, loadThemeForHost, isLoading: themeLoading } = useThemeStore();
    const hasLoadedTheme = useRef(false);

    // Load theme on mount
    useEffect(() => {
        if (!hasLoadedTheme.current) {
            const hostPrefix = getThemeHostPrefix();
            loadThemeForHost(hostPrefix);
            hasLoadedTheme.current = true;
        }
    }, [loadThemeForHost]);

    // MSAL event callback
    useEffect(() => {
        const callbackId = instance.addEventCallback(async (message) => {
            if (message.eventType === EventType.LOGIN_SUCCESS) {
                setUserName((message.payload as any).account.name);
                setAuthError(null);
            } else if (
                message.eventType === EventType.LOGIN_FAILURE ||
                message.eventType === EventType.ACQUIRE_TOKEN_FAILURE
            ) {
                // Surface the failure so the error screen renders instead of the
                // template auto-retrying the redirect (infinite loop).
                setAuthError(message.error ?? new Error('Authentication failed.'));
            }
        });
        callbackIdRef.current = callbackId;

        return () => {
            if (callbackIdRef.current) {
                instance.removeEventCallback(callbackIdRef.current);
            }
        };
    }, [instance]);

    const handleRetry = () => {
        clearInitialAuthError();
        setAuthError(null);
        instance.loginRedirect(loginRequest).catch((error) => setAuthError(error));
    };

    const handleAccessDeniedLogout = () => {
        logoutForAtlasAccessDenied().catch((error) => {
            clearAccessDenied();
            setAuthError(error);
        });
    };

    // Show loading indicator while theme loads
    if (themeLoading) {
        return <Loading modal />;
    }

    if (isAccessDenied) {
        return (
            <ThemeProvider theme={theme}>
                <AccessDenied
                    reason={reason}
                    onLogout={handleAccessDeniedLogout}
                    onRegister={async (contractCode) => {
                        await registerAtlasAccess(contractCode);
                        await queryClient.resetQueries({ queryKey: ['permissions'] });
                        clearAccessDenied();
                    }}
                />
            </ThemeProvider>
        );
    }

    // A fatal auth error stops the redirect loop: show the error + manual retry
    // instead of letting MsalAuthenticationTemplate re-trigger login automatically.
    if (authError) {
        return <AuthError error={authError} onRetry={handleRetry} />;
    }

    return (
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={loginRequest}
            errorComponent={({ error }) => <AuthError error={error} onRetry={handleRetry} />}
        >
            <Suspense fallback={<Loading modal />}>
                <ThemeProvider theme={theme}>
                    <AuthenticatedApp userName={userName} />
                </ThemeProvider>
            </Suspense>
        </MsalAuthenticationTemplate>
    );
}

interface AuthenticatedAppProps {
    userName: string;
}

function AuthenticatedApp({ userName }: AuthenticatedAppProps) {
    const isAuthenticated = useIsAuthenticated();
    const { data: permissions, isLoading, isFetching, error, refetch } = usePermissions(isAuthenticated);

    if (isLoading || isFetching) {
        return <Loading modal />;
    }

    if (error || !permissions) {
        return (
            <AuthError
                error={error ?? new Error('No se pudo validar el acceso Atlas para este cliente.')}
                onRetry={() => { void refetch(); }}
            />
        );
    }

    return <ProtectedData userName={userName} permissions={permissions} />;
}

interface ProtectedDataProps {
    userName: string;
    permissions: PermissionsResponse;
}

export function ProtectedData({ userName, permissions }: ProtectedDataProps) {
    const {
        impersonatedUser,
        setSuperuser,
        hasImpersonationError,
        setImpersonationError,
    } = useImpersonationStore();

    useEffect(() => {
        setSuperuser(permissions.isSuperuser);
    }, [permissions.isSuperuser, setSuperuser]);

    useEffect(() => {
        if (hasImpersonationError) {
            setImpersonationError(false);
        }
    }, [hasImpersonationError, setImpersonationError]);

    // The real superuser may have no subject profiles. Wait for an impersonated
    // user before mounting screens that request /api/subjects/profiles.
    if (permissions.isSuperuser && !impersonatedUser) {
        return <UserSelectionModal open onClose={() => undefined} />;
    }

    return <LayoutContainer userName={userName} />;
}

export default App;
